import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// A pill-shaped row with one slot per task. Slots render the reward emoji
// in grayscale until lit, full colour when lit, and run a one-shot `pop`
// animation on the unlit → lit transition. Per plan/03 §Animations & effects;
// reduced-motion is honoured via the .star-slot.pop CSS rule.

export interface StarShelfProps {
  emoji: string;
  count: number;
  litMask: boolean[];
  shelfLabel: string;
  // True once IDB hydration has completed. While false, the shelf renders
  // current state but suppresses the pop animation; the first render with
  // ready === true sets the baseline so pre-existing lit slots don't pop.
  ready?: boolean;
}

export interface StarShelfHandle {
  getSlot(index: number): HTMLElement | null;
}

export const StarShelf = forwardRef<StarShelfHandle, StarShelfProps>(function StarShelf(
  { emoji, count, litMask, shelfLabel, ready = true },
  ref,
) {
  const slotRefs = useRef<Array<HTMLElement | null>>([]);
  const baselineSetRef = useRef(false);
  const prevMaskRef = useRef<boolean[] | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      getSlot(index) {
        return slotRefs.current[index] ?? null;
      },
    }),
    [],
  );

  useEffect(() => {
    if (!ready) {
      // Don't establish a baseline yet — wait for hydration so we don't
      // diff against the React-state initial all-false mask.
      return;
    }
    if (!baselineSetRef.current) {
      baselineSetRef.current = true;
      prevMaskRef.current = litMask.slice();
      return;
    }
    const prev = prevMaskRef.current;
    if (prev === null) {
      prevMaskRef.current = litMask.slice();
      return;
    }
    const newlyLit: number[] = [];
    for (let i = 0; i < litMask.length; i++) {
      const before = prev[i] === true;
      const after = litMask[i] === true;
      if (!before && after) newlyLit.push(i);
    }
    prevMaskRef.current = litMask.slice();
    if (newlyLit.length === 0) return;
    for (const i of newlyLit) {
      const el = slotRefs.current[i];
      if (!el) continue;
      el.classList.add('pop');
      const onEnd = () => {
        el.classList.remove('pop');
        el.removeEventListener('animationend', onEnd);
      };
      el.addEventListener('animationend', onEnd);
    }
  }, [ready, litMask]);

  const slots = Array.from({ length: count }, (_, i) => {
    const lit = litMask[i] === true;
    return (
      <span
        key={i}
        ref={(el) => {
          slotRefs.current[i] = el;
        }}
        className={`star-slot${lit ? ' lit' : ''}`}
        data-lit={lit ? 'true' : 'false'}
      >
        {emoji}
      </span>
    );
  });

  return (
    <div className="star-shelf" aria-hidden="true">
      <span className="star-shelf-label">{shelfLabel}</span>
      <div className="star-shelf-slots">{slots}</div>
    </div>
  );
});
